import {
  BdsColorIconCalculatorDeviceOriginalSvg,
  BdsMonoCommonArrowRightLarge1Svg,
  getColor,
  SpacingHeight16,
  SpacingHeight20,
  SpacingHeight32,
  SpacingHeight40,
  useModal,
} from '@banksalad/bpl-web';
import { AccountType } from '@banksalad/idl/web/apis/v1/mydata';
import { convertToMoneyFormat } from '@banksalad/js-banksalad';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import sum from 'lodash/sum';
import { useState } from 'react';
import tw from 'twin.macro';

import { DividerHorizontal16 } from '@/page-src/product/components/divider-horizontal-16';
import { useGetDeposits } from '@/page-src/product/pages/deposits/hooks/use-get-deposits';
import { getPrimeConditionList } from '@/page-src/product/pages/installment-savings/pages/product-guid/utils/get-prime-condition-list';
import { getSavingsDepositCalculateInterest } from '@/page-src/product/pages/savings/controllers/get-calculated-savings-deposit-interest-rate';
import { ProductSavingsPrimeRateButton } from '@/src/@domain/savings/components/prime-rate-button';
import { SAVINGS_MAIN_COLOR } from '@/src/@domain/savings/constants';
import { ShoppingInputModal } from '@/src/@domain/shopping/components/input-modal';
import { trackClickToChangeCalculationResult } from '@/src/events/trackers/product/deposits/product-guid';
import { formatNumberToCommaSeparator } from '@/src/utils/currency';
import { parse2fToNumber } from '@/src/utils/parse-nf-to-number';

const tenBillion = 1000000000;
const oneMillion = 1000000;

export const ProductDepositCalculationSection = () => {
  const { result, isValidProduct } = useGetDeposits();
  const { open, asyncClose } = useModal();
  const { deposit, appliedCondition } = result;
  const [selectedSavingsInfo, setSelectedSavingsInfo] = useState<{
    interestRate2f: number;
    registrationMonths: number;
  }>({
    interestRate2f: appliedCondition.interestRateCondition.interestRate2f,
    registrationMonths: appliedCondition.interestRateCondition.registrationMonths,
  });

  const [amount, setAmount] = useState(
    deposit.maximumPaymentAmountKrw ? Number(deposit.maximumPaymentAmountKrw) : oneMillion
  );

  const [primeConditions, setPrimeConditions] = useState(
    getPrimeConditionList(
      deposit.primeRateConditions,
      appliedCondition,
      selectedSavingsInfo.registrationMonths
    )
  );

  if (deposit.descriptions.length === 0 || !isValidProduct) return null;

  const totalPrimeRate2f = sum(
    primeConditions
      .filter(condition => condition.isChecked)
      .map(condition => condition.condition.interestRate2f)
  );

  const totalInterestRate2f = selectedSavingsInfo.interestRate2f + totalPrimeRate2f;

  const { interestAmountAfterTax } = getSavingsDepositCalculateInterest({
    amount,
    rate: totalInterestRate2f / 100,
    period: selectedSavingsInfo.registrationMonths,
    accountType: AccountType.ACCOUNT_TYPE_DEPOSIT,
  });

  const openCalculationModal = () => {
    open({
      element: (
        <ShoppingInputModal
          title="적립 금액"
          initialValue={amount}
          minValue={100000}
          maxValue={
            deposit.maximumPaymentAmountKrw ? Number(deposit.maximumPaymentAmountKrw) : tenBillion
          }
          onComplete={(value: string) => {
            setAmount(Number(value));
            trackClickToChangeCalculationResult('금액');
            asyncClose();
          }}
          onClose={asyncClose}
        />
      ),
    });
  };

  return (
    <div css={tw`bg-white-900`}>
      <DividerHorizontal16 />
      <SpacingHeight32 />
      <h2 css={tw`text-head-20 font-bold mx-20`}>이자 계산기</h2>
      <SpacingHeight16 />
      <div css={tw`flex px-20 overflow-x-scroll justify-center`}>
        {deposit.interestRateConditions.map(condition => {
          const isSelectedMonth =
            condition.registrationMonths === selectedSavingsInfo.registrationMonths;
          return (
            <StyledButton
              onClick={() => {
                setSelectedSavingsInfo({
                  interestRate2f: condition.interestRate2f,
                  registrationMonths: condition.registrationMonths,
                });
                setPrimeConditions(
                  getPrimeConditionList(
                    deposit.primeRateConditions,
                    appliedCondition,
                    condition.registrationMonths
                  )
                );
                trackClickToChangeCalculationResult('가입기간');
              }}
              isSelected={isSelectedMonth}
              key={`interest_rate_${condition.registrationMonths}`}
            >
              <StyledButtonText isSelected={isSelectedMonth}>
                {condition.registrationMonths}개월
              </StyledButtonText>
              <StyledButtonSubText isSelected={isSelectedMonth}>
                기본 {parse2fToNumber(String(condition.interestRate2f))}%
              </StyledButtonSubText>
            </StyledButton>
          );
        })}
      </div>
      <SpacingHeight40 />
      <div css={tw`flex items-center flex-col`}>
        <TotalInterestRateText>
          금리 {parse2fToNumber(String(totalInterestRate2f))}%
        </TotalInterestRateText>
        <p css={tw`text-body-14`}>
          <span css={tw`text-gray-400`}>
            기본 {parse2fToNumber(String(selectedSavingsInfo.interestRate2f))}%
          </span>{' '}
          {primeConditions.length > 0 && (
            <>
              <span css={tw`text-gray-400`}>+</span>{' '}
              <TotalPrimeRateText>
                우대 {parse2fToNumber(String(totalPrimeRate2f))}%
              </TotalPrimeRateText>
            </>
          )}
        </p>
      </div>
      <SpacingHeight20 />
      <CalculationWrapper onClick={openCalculationModal}>
        <BdsColorIconCalculatorDeviceOriginalSvg />
        <p css={tw`ml-20`}>
          <div css={tw`font-medium text-gray-350 text-body-15 leading-[23px]`}>
            {convertToMoneyFormat({ money: amount, needWon: true, omitUnit: 0 })} 예금하면
          </div>
          <div css={tw`font-bold text-gray-100 text-body-16`}>
            총 세후 이자 {formatNumberToCommaSeparator(String(interestAmountAfterTax))}원
          </div>
        </p>
        <div css={tw`flex-grow text-right`}>
          <BdsMonoCommonArrowRightLarge1Svg width={20} height={20} color="gray-350" />
        </div>
      </CalculationWrapper>
      {primeConditions.length > 0 && (
        <>
          <p css={tw`text-body-15 font-medium text-gray-350 pl-24`}>우대금리가 반영된 금리예요</p>
          {primeConditions.map((primeCondition, index) => (
            <ProductSavingsPrimeRateButton
              primeCondition={primeCondition}
              primeConditions={primeConditions}
              setPrimeConditions={setPrimeConditions}
              index={index}
              key={primeCondition.condition.savingsPrimeRateConditionTypes.join(',')}
              isLast={index === primeConditions.length - 1}
              clickTracker={trackClickToChangeCalculationResult}
              pointColor={SAVINGS_MAIN_COLOR.DEPOSIT}
            />
          ))}
        </>
      )}
    </div>
  );
};

const StyledButton = styled.button<{ isSelected: boolean }>(
  tw`
        bg-white-900 border border-gray-700 border-solid  rounded-[10px]
        px-15 py-10 max-w-[82px]
        text-center
        flex-grow
      `,
  css`
    &:not(:first-of-type) {
      margin-left: 7px;
    }
  `,
  ({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${getColor('gray-200')};
      color: ${getColor('white-900')};
    `
);

const StyledButtonText = styled.p<{ isSelected: boolean }>(
  tw`
      font-bold text-body-13 mb-2 text-gray-100
      `,
  ({ isSelected }) =>
    isSelected &&
    css`
      color: ${getColor('white-900')};
    `
);

const StyledButtonSubText = styled.p<{ isSelected: boolean }>(
  tw`
        font-medium text-caption-10 mb-2 text-gray-400
        `,
  ({ isSelected }) =>
    isSelected &&
    css`
      color: ${getColor('white-900')};
    `
);

const TotalInterestRateText = styled.p(
  tw`font-extrabold text-head-28 mb-5`,
  css`
    color: ${getColor(SAVINGS_MAIN_COLOR.DEPOSIT)};
  `
);

const TotalPrimeRateText = styled.span(
  css`
    color: ${getColor(SAVINGS_MAIN_COLOR.DEPOSIT)};
  `
);

const CalculationWrapper = styled.div(tw`flex items-center p-24 cursor-pointer`);
